/**
 * Markdown 渲染：围栏代码块用 React 组件（复制按钮）；其余仍走 HTML 串
 */

import React from 'react';
import { CodeBlock } from '@/components/CodeBlock';

const escapeHtml = (text: string): string => {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return String(text).replace(/[&<>"']/g, (m) => map[m]);
};

export type MarkdownPart =
  | { type: 'text'; text: string }
  | { type: 'fenced'; lang: string; code: string };

/** 按 ``` 围栏切分，供 MarkdownContent / renderMarkdown 共用 */
export function splitMarkdownByFencedCode(content: string): MarkdownPart[] {
  if (!content) return [];
  const parts: MarkdownPart[] = [];
  const re = /```([\w-+#.]*)\s*\n([\s\S]*?)```/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    if (m.index > last) {
      parts.push({ type: 'text', text: content.slice(last, m.index) });
    }
    parts.push({
      type: 'fenced',
      lang: (m[1] || '').trim(),
      code: m[2].replace(/\n$/, '').trimEnd(),
    });
    last = re.lastIndex;
  }
  if (last < content.length) {
    parts.push({ type: 'text', text: content.slice(last) });
  }
  if (parts.length === 0) {
    parts.push({ type: 'text', text: content });
  }
  return parts;
}

function sanitizeLangClass(lang: string): string {
  return (lang || 'text').replace(/[^a-zA-Z0-9_-]/g, '') || 'text';
}

/** 不含围栏代码块：行内代码、粗斜体、换行 */
export function renderMarkdownSegment(text: string): string {
  if (!text) return '';

  let html = escapeHtml(text);

  html = html.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>');

  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');

  html = html.replace(/\n/g, '<br>');

  return html;
}

/**
 * 纯 HTML 版（无复制按钮），供非 React 场景；围栏块仍为 &lt;pre class="code-block"&gt;
 */
export const renderMarkdown = (text: string): string => {
  const parts = splitMarkdownByFencedCode(text);
  return parts
    .map((p) => {
      if (p.type === 'fenced') {
        const lang = sanitizeLangClass(p.lang);
        return `<pre class="code-block"><code class="enlighter-origin language-${lang}">${escapeHtml(p.code)}</code></pre>`;
      }
      return renderMarkdownSegment(p.text);
    })
    .join('');
};

export interface MarkdownContentProps {
  content: string;
  /**
   * 用户气泡内与 ▸ 同行：无围栏代码块时用 span 根节点，避免块级 div 把前缀挤到上一行
   */
  inlineRoot?: boolean;
}

export const MarkdownContent: React.FC<MarkdownContentProps> = ({
  content,
  inlineRoot = false,
}) => {
  const parts = splitMarkdownByFencedCode(content);
  if (parts.length === 0) return null;

  const hasFenced = parts.some((p) => p.type === 'fenced');
  const useInlineRoot = Boolean(inlineRoot && !hasFenced);
  const Root: 'div' | 'span' = useInlineRoot ? 'span' : 'div';

  return (
    <Root
      className={
        useInlineRoot
          ? 'sse-markdown-root sse-markdown-root--inline'
          : 'sse-markdown-root'
      }
    >
      {parts.map((p, i) =>
        p.type === 'fenced' ? (
          <CodeBlock key={i} language={p.lang} code={p.code} />
        ) : (
          <span
            key={i}
            className="sse-markdown-segment"
            dangerouslySetInnerHTML={{ __html: renderMarkdownSegment(p.text) }}
          />
        )
      )}
    </Root>
  );
};
